import { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { getUserRole, getUserToken } from '@/redux/user';
import { Option } from '@/components/atoms/Select';
import { useLazyGetGuestGroupsQuery } from '@/services/innovorder/guestGroup/guestGroup.endpoints';
import { useFormContext } from 'react-hook-form';
import { GetGuestGroupsPayload } from '@/services/innovorder/guestGroup/guestGroup.type';
import { ApiRequest } from '@/services/innovorder/request.types';
import { UserRoles } from '@/services/innovorder/user/user.type';
import { getGuestGroupOptions } from '@/utils/factories/hierarchization/hierarchization.utils';

type OptionsFetchingProps = {
    brandId: number;
    parentFieldId?: number;
    getter: (params: ApiRequest<GetGuestGroupsPayload>) => void;
    params: ApiRequest<GetGuestGroupsPayload>;
};

type FieldClearingProps = {
    field: string;
    isFetching: boolean;
    options: Option<number>[];
};

type UpdateCustomerFormVMProps = {
    brandId: number;
};

type UpdateInfoFormVM = {
    groups: Option<number>[];
    sections: Option<number>[];
    subSections: Option<number>[];
    isFetchingGroups: boolean;
    isFetchingSections: boolean;
    isFetchingSubSections: boolean;
    disableSection: boolean;
    disableSubSection: boolean;
    displayInfo: boolean;
};

const useOptionsFetching = ({ brandId, parentFieldId, getter, params }: OptionsFetchingProps) => {
    useEffect(() => {
        getter(parentFieldId ? { ...params, parentGuestGroupId: parentFieldId } : params);
    }, [brandId, parentFieldId]);
};

const useFieldClearing = ({ field, isFetching, options }: FieldClearingProps) => {
    const { setValue, watch } = useFormContext();
    const watchfield = watch(field)?.value;

    useEffect(() => {
        if (!isFetching && field) {
            const exist = options.some((option) => option.value === watchfield);
            if (!exist) {
                setValue(field, null);
            }
        }
    }, [options, isFetching, field]);
};

export const useGuestGroupFieldOptions = ({ brandId }: UpdateCustomerFormVMProps): UpdateInfoFormVM => {
    const { watch } = useFormContext();
    const token = useSelector(getUserToken);
    const userRole = useSelector(getUserRole);
    const isBrand = userRole === UserRoles.brand;

    // Watch the selected group and section
    const watchGroupId = watch('groupId')?.value;
    const watchSectionId = watch('sectionId')?.value;

    const [getGroups, { isFetching: isFetchingGroups, data: groupsData }] = useLazyGetGuestGroupsQuery();
    const [getSections, { isFetching: isFetchingSections, data: sectionsData }] = useLazyGetGuestGroupsQuery();
    const [getSubSections, { isFetching: isFetchingSubSections, data: subSectionsData }] = useLazyGetGuestGroupsQuery();

    // Memoize guest groups for performance
    const groups = useMemo(() => getGuestGroupOptions(groupsData || []), [groupsData]);
    const sections = useMemo(
        () => getGuestGroupOptions(watchGroupId && sectionsData ? sectionsData : []),
        [sectionsData, watchGroupId],
    );
    const subSections = useMemo(
        () => getGuestGroupOptions(watchSectionId && subSectionsData ? subSectionsData : []),
        [subSectionsData, watchSectionId],
    );

    // Clear fields if they don't exist in the options
    useFieldClearing({ field: 'groupId', isFetching: isFetchingGroups, options: groups });
    useFieldClearing({ field: 'sectionId', isFetching: isFetchingSections, options: sections });
    useFieldClearing({ field: 'subSectionId', isFetching: isFetchingSubSections, options: subSections });

    // Fetch guest groups
    useOptionsFetching({ brandId, getter: getGroups, params: { token, brandId, depth: 0 } });
    useOptionsFetching({
        brandId,
        parentFieldId: watchGroupId,
        getter: getSections,
        params: { token, brandId, depth: 1 },
    });
    useOptionsFetching({
        brandId,
        parentFieldId: watchSectionId,
        getter: getSubSections,
        params: { token, brandId, depth: 2 },
    });

    // Disable section and sub-section if the group is not selected
    const disableSection = useMemo(() => !watchGroupId, [watchGroupId]);
    // Disable sub-section if the section is not selected
    const disableSubSection = useMemo(() => !watchSectionId, [watchSectionId]);

    return {
        groups,
        sections,
        subSections,
        isFetchingGroups,
        isFetchingSections,
        isFetchingSubSections,
        disableSection,
        disableSubSection,
        displayInfo: isBrand,
    };
};
